/**
 * Translations module for the Source theme
 * Handles loading and switching between different language translations
 */

let currentLanguage = 'en';
let translations = {};

// Default translations for common elements
const defaultTranslations = {
    en: {
        navigation: {
            home: 'Home',
            about: 'About',
            contact: 'Contact',
            search: 'Search',
            signin: 'Sign In',
            signup: 'Sign Up'
        },
        common: {
            readMore: 'Read More',
            loading: 'Loading...',
            error: 'Error',
            success: 'Success'
        }
    },
    zh: {
        navigation: {
            home: '首页',
            about: '关于',
            contact: '联系我们',
            search: '搜索',
            signin: '登录',
            signup: '注册'
        },
        common: {
            readMore: '阅读更多',
            loading: '加载中...',
            error: '错误',
            success: '成功'
        }
    }
};

/**
 * Initialize the translation system
 * @param {string} defaultLang - The default language to use
 */
function initTranslations(defaultLang = 'en') {
    // First try to detect language from URL path
    const pathLang = detectLanguageFromPath();

    // Then check localStorage
    const savedLang = localStorage.getItem('ghost-theme-language');

    // Use path language > saved language > browser language > default
    if (pathLang && isLanguageSupported(pathLang)) {
        currentLanguage = pathLang;
    } else if (savedLang && isLanguageSupported(savedLang)) {
        currentLanguage = savedLang;
    } else {
        // Try to detect from browser
        const browserLang = navigator.language.split('-')[0];
        // Only use browser language if we have translations for it
        if (isLanguageSupported(browserLang)) {
            currentLanguage = browserLang;
        } else {
            currentLanguage = defaultLang;
        }
    }

    // Load the translations
    loadTranslations(currentLanguage);

    // Update the language selector if it exists
    updateLanguageSelector();

    // Add event listener for language selector
    // Add event listener for language selectors
    const selectors = document.querySelectorAll('.gh-language-selector');
    selectors.forEach(selector => {
        selector.addEventListener('change', function() {
            const newLang = this.value.replace(/^\//, ''); // Remove leading slash if present
            if (isLanguageSupported(newLang)) {
                changeLanguage(newLang);
            }
        });
    });
}

/**
 * Detect language from URL path
 * @return {string|null} - The detected language code or null
 */
function detectLanguageFromPath() {
    const path = window.location.pathname;
    const pathParts = path.split('/').filter(Boolean);

    // Check if the first part of the path is a language code
    if (pathParts.length > 0) {
        const potentialLang = pathParts[0];
        if (isLanguageSupported(potentialLang)) {
            return potentialLang;
        }
    }

    return null;
}

/**
 * Check if a language is supported
 * @param {string} lang - The language code to check
 * @return {boolean} - Whether the language is supported
 */
function isLanguageSupported(lang) {
    // This could be enhanced to dynamically check available translation files
    return ['en', 'fr', 'ar', 'es', 'de', 'ja', 'zh', 'zh-CN', 'zh-TW'].includes(lang);
}

/**
 * Check if a language is RTL (Right-to-Left)
 * @param {string} lang - The language code to check
 * @return {boolean} - Whether the language is RTL
 */
function isRTL(lang) {
    // List of RTL languages
    return ['ar', 'he', 'fa', 'ur'].includes(lang);
}

/**
 * Load translations for a specific language
 * @param {string} lang - The language code to load
 */
function loadTranslations(lang) {
    // First apply default translations for the language
    translations = defaultTranslations[lang] || defaultTranslations['en'];

    // Then try to load additional translations from file
    fetch(`${window.location.origin}/assets/translations/${lang}.json`)
        .then(response => {
            if (!response.ok) {
                throw new Error(`Failed to load translations for ${lang}`);
            }
            return response.json();
        })
        .then(data => {
            // Merge the loaded translations with defaults
            translations = deepMerge(translations, data);
            // Apply translations to the page
            applyTranslations();

            // Update HTML lang attribute
            document.documentElement.setAttribute('lang', lang);
        })
        .catch(error => {
            console.error('Error loading translations:', error);
            // Still apply the default translations
            applyTranslations();
            document.documentElement.setAttribute('lang', lang);
        });
}

/**
 * Deep merge two objects
 * @param {Object} target - The target object
 * @param {Object} source - The source object
 * @return {Object} - The merged object
 */
function deepMerge(target, source) {
    const result = { ...target };
    for (const key in source) {
        if (source[key] instanceof Object && key in target && target[key] instanceof Object) {
            result[key] = deepMerge(target[key], source[key]);
        } else {
            result[key] = source[key];
        }
    }
    return result;
}

/**
 * Change the current language
 * @param {string} lang - The language code to switch to
 */
function changeLanguage(lang) {
    if (!isLanguageSupported(lang) || lang === currentLanguage) {
        return;
    }

    // Store the language preference
    localStorage.setItem('ghost-theme-language', lang);

    // Update current language
    currentLanguage = lang;

    // Load and apply new translations
    loadTranslations(lang);

    // Update the language selector
    updateLanguageSelector();

    // Dispatch a custom event that other parts of the application can listen to
    const event = new CustomEvent('languageChanged', { detail: { language: lang } });
    document.dispatchEvent(event);
}

/**
 * Construct a URL for the given language
 * @param {string} lang - The language code
 * @return {string} - The URL for the given language
 */
function constructLanguageUrl(lang) {
    // Start with the origin and path
    let newUrl = window.location.origin + window.location.pathname;

    // Parse existing query parameters
    const searchParams = new URLSearchParams(window.location.search);

    // Set or update the lang parameter
    searchParams.set('lang', lang);

    // Reconstruct URL with updated query parameters
    newUrl += '?' + searchParams.toString();

    // Add hash if it exists
    if (window.location.hash) {
        newUrl += window.location.hash;
    }

    return newUrl;
}
/**
 * Update the language selector to show the current language
 */
function updateLanguageSelector() {
    const selectors = document.querySelectorAll('.gh-language-selector');
    selectors.forEach(selector => {
        // Look for the option that corresponds to the current language
        const options = selector.options;
        for (let i = 0; i < options.length; i++) {
            const option = options[i];
            const optionLang = option.value.replace(/^\//, ''); // Remove leading slash

            if (optionLang === currentLanguage) {
                selector.selectedIndex = i;
                break;
            }
        }
    });
}

/**
 * Get a translated string
 * @param {string} key - The translation key in dot notation (e.g., 'navigation.signin')
 * @param {string} defaultText - The default text to use if the key is not found
 * @return {string} - The translated string
 */
function t(key, defaultText) {
    // Split the key by dots to navigate the translations object
    const parts = key.split('.');
    let value = translations;

    // Navigate through the translations object
    for (const part of parts) {
        if (value && typeof value === 'object' && part in value) {
            value = value[part];
        } else {
            return defaultText || key;
        }
    }

    return value || defaultText || key;
}

/**
 * Apply translations to all elements with data-i18n attributes
 */
function applyTranslations() {
    // Translate text content for elements with data-i18n attribute
    const elements = document.querySelectorAll('[data-i18n]');
    elements.forEach(element => {
        const key = element.getAttribute('data-i18n');
        const defaultText = element.textContent;
        element.textContent = t(key, defaultText);
    });

    // Translate attributes for elements with data-i18n-attr attribute
    const attrElements = document.querySelectorAll('[data-i18n-attr]');
    attrElements.forEach(element => {
        const attrSpec = element.getAttribute('data-i18n-attr');
        // Format is 'attribute:translation.key'
        const parts = attrSpec.split(':');
        if (parts.length === 2) {
            const attrName = parts[0];
            const key = parts[1];
            const defaultValue = element.getAttribute(attrName) || '';
            element.setAttribute(attrName, t(key, defaultValue));
        }
    });

    // Set HTML dir attribute based on language direction
    document.documentElement.setAttribute('dir', isRTL(currentLanguage) ? 'rtl' : 'ltr');
}

// Export the functions to make them available globally
window.initTranslations = initTranslations;
window.changeLanguage = changeLanguage;
window.t = t;

// Initialize translations when the DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
    initTranslations();
});